import { call, delay } from 'redux-saga/effects';
import { isRetryableError, isStatusError, isTransportFailure } from '@/utils/networkErrors';

// ---------- shared backoff math ----------

const computeBackoffDelay = (attempt: number, baseMs: number, maxMs: number): number => {
  const exp = baseMs * 2 ** attempt;
  const jitter = Math.floor(Math.random() * baseMs);
  return Math.min(exp + jitter, maxMs);
};

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The attempt budget for one failure: `transportAttempts` when the request never got an HTTP
 * response (a dropped connection — Safari's "Load failed", Chrome's "Failed to fetch", wrapped or
 * not), `attempts` for everything else. A real 5xx keeps the short budget. (TEAM-15682)
 */
const attemptBudget = (error: unknown, attempts: number, transportAttempts?: number): number =>
  transportAttempts !== undefined && isTransportFailure(error) ? transportAttempts : attempts;

// ---------- retryOnceOnStatuses (saga, single retry on specific statuses) ----------

export const DEFAULT_RETRY_BACKOFF_MS = 250;

export interface RetryOptions {
  statuses: readonly number[];
  backoffMs?: number;
}

/**
 * Wrap a saga `call` so that a single response matching one of `options.statuses`
 * triggers a silent retry after `options.backoffMs` (default 250 ms). Errors with
 * any other status, or no status field, propagate immediately.
 *
 * Designed for promise-returning SDK functions; passing a saga generator is
 * not supported (use redux-saga's built-in `retry` for that).
 */
export function* retryOnceOnStatuses<Args extends unknown[], R>(
  fn: (...args: Args) => R,
  options: RetryOptions,
  ...args: Args
): Generator<unknown, Awaited<R>> {
  const { statuses, backoffMs = DEFAULT_RETRY_BACKOFF_MS } = options;
  try {
    return (yield call(fn, ...args)) as Awaited<R>;
  } catch (error) {
    if (!isStatusError(error, statuses)) throw error;
    yield delay(backoffMs);
    return (yield call(fn, ...args)) as Awaited<R>;
  }
}

// ---------- retryWithBackoff (saga, exponential backoff) ----------

export const DEFAULT_RETRY_ATTEMPTS = 3;
export const DEFAULT_RETRY_BASE_MS = 300;
export const DEFAULT_RETRY_MAX_MS = 2000;

/**
 * Total attempts for a read that fails with no HTTP response: 1 + 5 retries. With the default
 * backoff that spans roughly 6 seconds (300, 600, 1200, 2000, 2000 ms, plus jitter) — long enough
 * to ride out a brief signal drop on a phone, which is the dominant menu-load failure on iOS
 * (COO-WEBAPP-4ZJ). (TEAM-15682)
 */
export const TRANSPORT_RETRY_ATTEMPTS = 6;

export interface RetryWithBackoffOptions {
  /** Total attempts including the first call. Defaults to 3. */
  attempts?: number;
  /** Base delay for the exponential backoff in ms. Defaults to 300. */
  baseMs?: number;
  /** Upper bound on each individual delay in ms. Defaults to 2000. */
  maxMs?: number;
  /** Predicate that decides whether an error is worth retrying. */
  predicate?: (error: unknown) => boolean;
  /**
   * Total attempts (including the first call) when the failure is a dropped connection with no
   * HTTP response ({@link isTransportFailure}). Opt-in: when omitted, every failure uses
   * `attempts`. Pass {@link TRANSPORT_RETRY_ATTEMPTS} for first-load reads a diner is waiting on,
   * so a phone that loses signal for a moment recovers instead of failing in ~1 s. (TEAM-15682)
   */
  transportAttempts?: number;
  /**
   * Invoked with `(error, attempts)` when the final attempt fails (budget
   * exhausted or a non-retryable error) — the single place to report to
   * Sentry/telemetry so a recovered blip stays silent and a sustained one
   * reports once, not once per attempt.
   *
   * May throw to replace the propagated error (e.g. wrapping into an
   * enriched error type). The thrown value becomes the rejection reason;
   * the original error is not re-thrown.
   */
  onFinalFailure?: (error: unknown, attempts: number) => void;
}

/**
 * Wrap a saga `call` with exponential-backoff retries for transient failures.
 * Designed for idempotent reads — never use on writes/mutations.
 *
 * Defaults: 3 attempts, base 300 ms, max 2000 ms, predicate {@link isRetryableError}.
 * The final failure is rethrown unchanged so callers can keep their existing
 * try/catch and Sentry reporting.
 *
 * @example
 *   const data = yield* retryWithBackoff(getMenu, { attempts: 3 }, args, { client });
 */
export function* retryWithBackoff<Args extends unknown[], R>(
  fn: (...args: Args) => R,
  options: RetryWithBackoffOptions = {},
  ...args: Args
): Generator<unknown, Awaited<R>> {
  const {
    attempts = DEFAULT_RETRY_ATTEMPTS,
    baseMs = DEFAULT_RETRY_BASE_MS,
    maxMs = DEFAULT_RETRY_MAX_MS,
    predicate = isRetryableError,
    onFinalFailure,
    transportAttempts,
  } = options;

  // The budget depends on the failure, so the loop ends from inside the catch.
  for (let attempt = 0; ; attempt++) {
    try {
      return (yield call(fn, ...args)) as Awaited<R>;
    } catch (error) {
      const hasMore = attempt < attemptBudget(error, attempts, transportAttempts) - 1;
      if (!hasMore || !predicate(error)) {
        onFinalFailure?.(error, attempt + 1);
        throw error;
      }
      yield delay(computeBackoffDelay(attempt, baseMs, maxMs));
    }
  }
}

// ---------- retryPromiseWithBackoff (promise, exponential backoff) ----------

/** Same options as {@link RetryWithBackoffOptions}; the promise flavor shares the schedule. */
export type RetryPromiseOptions = RetryWithBackoffOptions;

/**
 * Wrap a promise-returning function with exponential-backoff retries for
 * transient failures. The plain-promise counterpart to {@link retryWithBackoff}
 * — use this from page-level `getInitialProps`, React-Query `queryFn`s, or
 * anywhere outside a saga.
 *
 * Defaults: 3 attempts, base 300 ms, max 2000 ms, predicate {@link isRetryableError}.
 * The final failure is rethrown unchanged.
 */
export async function retryPromiseWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryPromiseOptions = {},
): Promise<T> {
  const {
    attempts = DEFAULT_RETRY_ATTEMPTS,
    baseMs = DEFAULT_RETRY_BASE_MS,
    maxMs = DEFAULT_RETRY_MAX_MS,
    predicate = isRetryableError,
    onFinalFailure,
    transportAttempts,
  } = options;

  // The budget depends on the failure, so the loop ends from inside the catch.
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const hasMore = attempt < attemptBudget(error, attempts, transportAttempts) - 1;
      if (!hasMore || !predicate(error)) {
        onFinalFailure?.(error, attempt + 1);
        throw error;
      }
      await sleep(computeBackoffDelay(attempt, baseMs, maxMs));
    }
  }
}

/**
 * Tighter retry budget for server-side (`getServerSideProps`) reads. Every retry
 * here blocks TTFB for diners hitting the page during a coo-api incident, and
 * `queryOnServer` already degrades gracefully — a rejected prefetch just leaves
 * the cache empty and the client re-fetches with its own (default) budget. So
 * cap the SSR cost at one short retry (~150–300 ms) instead of the default
 * ~0.9–1.5 s, keeping a degraded edge from pushing the render toward the
 * platform request timeout.
 */
export const SSR_RETRY_OPTIONS: RetryPromiseOptions = { attempts: 2, baseMs: 150, maxMs: 400 };

// ---------- React Query retry policy (transport-aware) ----------

/** React Query's `retry` counts retries on top of the first attempt, not total attempts. */
const TRANSPORT_MAX_RETRIES = TRANSPORT_RETRY_ATTEMPTS - 1;

/**
 * React Query `retry` callback with the same split as `transportAttempts`: a failure with no HTTP
 * response gets {@link TRANSPORT_RETRY_ATTEMPTS} in total, any other retryable failure (5xx, 429,
 * a non-JSON gateway page) keeps `maxRetries`. Pair with {@link queryRetryDelay}. (TEAM-15682)
 */
export const transportAwareRetry =
  (maxRetries: number) =>
  (failureCount: number, error: unknown): boolean =>
    isTransportFailure(error)
      ? failureCount < TRANSPORT_MAX_RETRIES
      : failureCount < maxRetries && isRetryableError(error);

/**
 * React Query `retryDelay`: exponential from {@link DEFAULT_RETRY_BASE_MS}, capped at
 * {@link DEFAULT_RETRY_MAX_MS} — 300, 600, 1200, 2000, 2000 ms. No jitter, so the first two
 * delays (the whole 5xx budget) stay exactly what they were before TEAM-15682.
 */
export const queryRetryDelay = (attemptIndex: number): number =>
  Math.min(DEFAULT_RETRY_BASE_MS * 2 ** attemptIndex, DEFAULT_RETRY_MAX_MS);
